/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ColDef, GridOptions } from 'ag-grid-community';
import { extend, isFunction, isNil, sortBy, upperFirst } from 'lodash';

import { GridDefinition } from './grid-definition';
import { GridLayout, toGridDirection } from './grid-layout';

const DEFAULT_GRID_COLUMN_WIDTH = 200;

export class LayoutBasedGridDefinition implements GridDefinition {
  private _layout: GridLayout;

  get layout(): GridLayout {
    return this._layout;
  }

  bind(layout: GridLayout): void {
    if (isNil(layout)) {
      throw new Error('LayoutBasedGridDefinition: layout is nil');
    }
    this._layout = layout;
  }

  build(): GridOptions {
    const layout = this._layout;

    if (isNil(layout)) {
      throw new Error('LayoutBasedGridDefinition: grid definition is used before it was bound to the layout');
    }

    const columnDefs = this.getConfig<Hash<ColDef>>('columnDefs');
    const options = this.getConfig<GridOptions>('options') || this.getConfig<GridOptions>('gridOptions') || {};

    const layoutColumnsDefs: ColDef[] = sortBy(layout.columns, 'position').map((column) => ({
      colId: column.column,
      field: column.column,
      width: column.width || DEFAULT_GRID_COLUMN_WIDTH,
      sortable: column.sortable,
      resizable: column.resizable,
      suppressMovable: true,
      hide: !column.visible,
      sort: layout.sort && layout.sort.field === column.column ? toGridDirection(layout.sort.direction) : void 0,
    }));

    if (columnDefs) {
      layoutColumnsDefs.forEach((def) => {
        const column = columnDefs[<string>def.colId];

        if (column) {
          extend(def, column);
        }
      });
    }

    options.columnDefs = layoutColumnsDefs;

    return options;
  }

  buildColumnDefs?(): Hash<ColDef>;

  buildGridOptions?(): GridOptions;

  private getConfig<T>(name: string): Maybe<T> {
    if (!name) {
      return;
    }

    const method = this[`build${upperFirst(name)}`];

    return isFunction(method) ? method.call(this) : this[name];
  }

}
